import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Camera, CheckCircle2, ExternalLink, Image as ImageIcon, Inbox, Loader2,
  RefreshCw, Sparkles, X,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

// "Scan a product" — the phone way into the pipeline. Point the camera
// at a can or bottle; the backend reads the label, checks it against
// the org's brands and either points at the tracked brand or offers
// to land it in the review queue (or approve it straight away).
//
// On a phone it is a bottom sheet (same o-sheet chrome as Filters); on
// desktop the same flow runs in the centred dialog with a file picker
// in place of the camera. The photo never leaves the request — nothing
// is stored server-side.

const STROKE = 2.75;

// Phones hand back 12 MP JPEGs; the label is perfectly readable at a
// fraction of that and the upload is what the user waits on, so shrink
// before sending. `imageOrientation: 'from-image'` bakes the EXIF
// rotation in so a portrait shot doesn't arrive sideways.
const MAX_EDGE = 1280;

async function shrinkImage(file) {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) return file;
    return new File([blob], 'product.jpg', { type: 'image/jpeg' });
  } catch {
    // HEIC on a browser that can't decode it, or a canvas quota hit —
    // send the original and let the backend decide.
    return file;
  }
}

function confidenceLabel(value) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  if (pct >= 80) return { text: `${pct}% sure`, tone: 'var(--color-accent-2-700)' };
  if (pct >= 50) return { text: `${pct}% sure`, tone: 'var(--color-text)' };
  return { text: `only ${pct}% sure — check the name`, tone: 'var(--color-accent-700)' };
}

function PhotoCaptureSheet({ onClose, onAdded, autoOpenCamera = false }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const [phase, setPhase] = useState('pick'); // pick | identifying | result | saving | added | error
  const [preview, setPreview] = useState(null);
  const [identified, setIdentified] = useState(null);
  const [existing, setExisting] = useState(null);
  const [queued, setQueued] = useState(null); // pending row already in the review queue
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', website: '', instagram_handle: '', category: '' });
  const [addedBrand, setAddedBrand] = useState(null); // { kind: 'pending' | 'brand', id, name }

  // Deep link (`/brands?scan=1`, the PWA shortcut) opens the camera
  // without a second tap. Browsers only honour the programmatic click
  // when it follows a user gesture, so this is best-effort — the
  // buttons are right there if it is ignored.
  useEffect(() => {
    if (autoOpenCamera && isMobile) cameraInputRef.current?.click();
  }, [autoOpenCamera, isMobile]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const reset = () => {
    setPhase('pick');
    setPreview(null);
    setIdentified(null);
    setExisting(null);
    setQueued(null);
    setMessage(null);
    setError(null);
    setAddedBrand(null);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // let the same photo be picked again after a retake
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    setPhase('identifying');
    setError(null);
    try {
      const upload = await shrinkImage(file);
      const result = await api.identifyBrandPhoto(upload);
      const found = result.identified || {};
      setIdentified(found);
      setExisting(result.existing_brand || null);
      setQueued(result.pending_brand || null);
      setMessage(result.message || null);
      setForm({
        name: found.brand_name || '',
        website: found.website || '',
        instagram_handle: found.instagram_handle || '',
        category: found.category || '',
      });
      setPhase('result');
    } catch (err) {
      setError(err.message || 'Could not identify the photo.');
      setPhase('error');
    }
  };

  const pendingPayload = () => ({
    name: form.name.trim(),
    website: form.website.trim() || null,
    instagram_handle: form.instagram_handle.trim() || null,
    tiktok_handle: identified?.tiktok_handle || null,
    category: form.category.trim() || null,
    country: identified?.country || null,
    parent_company: identified?.parent_company || null,
    description: identified?.product_name ? `Spotted product: ${identified.product_name}` : null,
    confidence_score: identified?.confidence ?? null,
    source: 'photo_capture',
    metadata: {
      product_name: identified?.product_name || null,
      model: identified?.model || null,
      reason: identified?.reason || null,
    },
  });

  // Save for review: a pending row the Review inbox picks up.
  // Add & research: the same row, approved on the spot, so the brand
  // exists now and enrichment runs in the background like an approval
  // from the Review page.
  const submit = async (approveNow) => {
    if (!form.name.trim()) return;
    setPhase('saving');
    setError(null);
    try {
      const created = await api.createPendingBrand(pendingPayload());
      if (!created.created && created.existing_brand) {
        // The corrected name matched a tracked brand after all.
        setExisting(created.existing_brand);
        setMessage(created.message || null);
        setPhase('result');
        return;
      }
      // `created: false` with a pending row means the backend found the
      // brand already queued and handed that row back instead of
      // inserting a twin — approve / point at it like our own.
      const row = created.pending;
      const alreadyQueued = !created.created;
      if (approveNow) {
        try {
          const approved = await api.approvePendingBrand(row.id);
          const brand = approved.brand;
          setAddedBrand({ kind: 'brand', id: brand.id, name: brand.name, duplicate: Boolean(approved.duplicate) });
        } catch (approveErr) {
          // The row exists either way — say so rather than inviting a
          // retry that would land a second copy in the queue.
          setAddedBrand({ kind: 'pending', id: row.id, name: row.name, approveFailed: approveErr.message });
        }
      } else {
        setAddedBrand({ kind: 'pending', id: row.id, name: row.name, alreadyQueued });
      }
      // After the approve, not before it — the parent re-counts the
      // review badge here, and an approved row no longer counts.
      onAdded?.();
      setPhase('added');
    } catch (err) {
      setError(err.message || 'Could not add the brand.');
      setPhase('result');
    }
  };

  const openBrand = (id) => {
    onClose();
    navigate(`/brands/${id}`);
  };

  const title = phase === 'added' ? 'Added' : 'Scan a product';

  const inputs = (
    <>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={handleFile}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFile}
      />
    </>
  );

  const previewBox = preview && (
    <div
      style={{
        width: '100%', aspectRatio: '4 / 3', maxHeight: 220, borderRadius: 20, overflow: 'hidden',
        background: 'var(--color-neutral-200)', display: 'grid', placeItems: 'center',
      }}
    >
      <img src={preview} alt="The product you photographed" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </div>
  );

  let body = null;
  let actions = null;

  if (phase === 'pick') {
    body = (
      <>
        <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
          Take a picture of the can, bottle or shelf tag. We read the label,
          check whether you already track the brand, and add it to your
          pipeline if not.
        </p>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {isMobile && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ height: 56, fontSize: 16, justifyContent: 'center' }}
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera size={20} strokeWidth={STROKE} /> Take a photo
            </button>
          )}
          <button
            type="button"
            className={isMobile ? 'btn btn-secondary' : 'btn btn-primary'}
            style={{ height: isMobile ? 48 : 44, justifyContent: 'center' }}
            onClick={() => libraryInputRef.current?.click()}
          >
            <ImageIcon size={18} strokeWidth={STROKE} /> {isMobile ? 'Choose from library' : 'Choose a photo'}
          </button>
        </div>
      </>
    );
    actions = (
      <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
    );
  } else if (phase === 'identifying') {
    body = (
      <>
        {previewBox}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.8 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Reading the label…
        </div>
      </>
    );
    actions = (
      <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
    );
  } else if (phase === 'error') {
    body = (
      <>
        {previewBox}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-200)', fontSize: 13 }}>
          <AlertTriangle size={15} strokeWidth={STROKE} style={{ flex: 'none', marginTop: 2 }} />
          <span>{error}</span>
        </div>
      </>
    );
    actions = (
      <>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        <button type="button" className="btn btn-primary" onClick={reset}>
          <RefreshCw size={16} strokeWidth={STROKE} /> Try again
        </button>
      </>
    );
  } else if (phase === 'added' && addedBrand) {
    const isBrand = addedBrand.kind === 'brand';
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-2-100, var(--color-neutral-100))', fontSize: 14 }}>
          <CheckCircle2 size={18} strokeWidth={STROKE} style={{ flex: 'none', color: 'var(--color-accent-2-700)' }} />
          <span>
            <strong>{addedBrand.name}</strong>{' '}
            {isBrand
              ? (addedBrand.duplicate
                ? 'was already tracked — opened the existing brand.'
                : 'is now in your brands. Research is filling the profile in the background.')
              : (addedBrand.approveFailed
                ? `was saved to the review queue, but approving it failed (${addedBrand.approveFailed}). Approve it from Review.`
                : (addedBrand.alreadyQueued
                  ? 'was already waiting in the review queue — nothing new was added. Approve it there to run research.'
                  : 'is waiting in the review queue. Approve it there to run research.'))}
          </span>
        </div>
      </>
    );
    actions = (
      <>
        <button type="button" className="btn btn-secondary" onClick={reset}>
          <Camera size={16} strokeWidth={STROKE} /> Scan another
        </button>
        {isBrand ? (
          <button type="button" className="btn btn-primary" onClick={() => openBrand(addedBrand.id)}>
            Open brand page <ExternalLink size={14} strokeWidth={STROKE} />
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={() => { onClose(); navigate('/brands/research/pending'); }}>
            <Inbox size={16} strokeWidth={STROKE} /> Review now
          </button>
        )}
      </>
    );
  } else if (phase === 'result' || phase === 'saving') {
    const saving = phase === 'saving';
    const confidence = confidenceLabel(identified?.confidence);

    if (existing) {
      body = (
        <>
          {previewBox}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.6 }}>Already in your brands</span>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: 22 }}>{existing.name}</span>
            <span style={{ fontSize: 13, opacity: 0.75 }}>
              {[existing.category, existing.country].filter(Boolean).join(' · ') || 'Tracked brand'}
              {identified?.product_name ? ` · seen: ${identified.product_name}` : ''}
            </span>
            {identified?.brand_name && identified.brand_name.toLowerCase() !== existing.name.toLowerCase() && (
              <span style={{ fontSize: 12, opacity: 0.6 }}>Read on the label as “{identified.brand_name}”.</span>
            )}
          </div>
        </>
      );
      actions = (
        <>
          <button type="button" className="btn btn-secondary" onClick={reset}>
            <Camera size={16} strokeWidth={STROKE} /> Scan another
          </button>
          <button type="button" className="btn btn-primary" onClick={() => openBrand(existing.id)}>
            Open brand page <ExternalLink size={14} strokeWidth={STROKE} />
          </button>
        </>
      );
    } else {
      const recognised = Boolean(identified?.brand_name);
      const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
      body = (
        <>
          {previewBox}
          {recognised ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.6 }}>
                {queued ? 'Already in your review queue' : 'Not in your brands yet'}
              </span>
              {queued && (
                <span style={{ fontSize: 13, opacity: 0.8 }}>
                  Waiting for approval as “{queued.name}”. Add &amp; research approves that row.
                </span>
              )}
              {identified?.product_name && (
                <span style={{ fontSize: 13, opacity: 0.75 }}>Seen: {identified.product_name}</span>
              )}
              {confidence && <span style={{ fontSize: 12, color: confidence.tone }}>{confidence.text}</span>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-200)', fontSize: 13 }}>
              <AlertTriangle size={15} strokeWidth={STROKE} style={{ flex: 'none', marginTop: 2 }} />
              <span>
                {message || "Couldn't read a brand on that photo."} Retake with the label
                filling the frame, or type the name below.
              </span>
            </div>
          )}
          <div className="field">
            <label>Brand name</label>
            <input className="input" value={form.name} onChange={set('name')} placeholder="e.g. Olipop" disabled={saving} />
          </div>
          <div className="field">
            <label>Website (optional)</label>
            <input className="input" type="text" inputMode="url" value={form.website} onChange={set('website')} placeholder="drinkexample.com" disabled={saving} />
          </div>
          <div className="field">
            <label>Instagram (optional)</label>
            <input className="input" value={form.instagram_handle} onChange={set('instagram_handle')} placeholder="handle" disabled={saving} />
          </div>
          <div className="field">
            <label>Category (optional)</label>
            <input className="input" value={form.category} onChange={set('category')} placeholder="e.g. Energy Drink" disabled={saving} />
          </div>
          {error && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-accent-700)' }}>{error}</p>
          )}
          <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>
            Research fills socials, HQ and founders once the brand is approved.
          </p>
        </>
      );
      actions = (
        <>
          <button type="button" className="btn btn-secondary" onClick={reset} disabled={saving} title="Retake the photo">
            <RefreshCw size={16} strokeWidth={STROKE} /> {isMobile ? '' : 'Retake'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => submit(false)} disabled={saving || !form.name.trim()}>
            <Inbox size={16} strokeWidth={STROKE} /> Save for review
          </button>
          <button type="button" className="btn btn-primary" onClick={() => submit(true)} disabled={saving || !form.name.trim()}>
            {saving ? <Loader2 size={16} strokeWidth={STROKE} className="spin" /> : <Sparkles size={16} strokeWidth={STROKE} />}
            {saving ? 'Adding…' : 'Add & research'}
          </button>
        </>
      );
    }
  }

  if (isMobile) {
    return (
      <>
        {inputs}
        <div className="o-sheet-backdrop" onClick={onClose} />
        <div className="o-sheet" role="dialog" aria-modal="true" aria-label={title}>
          <div className="o-sheet-head">
            <span className="o-sheet-grip" />
            <div className="o-sheet-title-row">
              <h3 style={{ margin: 0 }}>{title}</h3>
              <button type="button" className="btn btn-icon btn-secondary" aria-label="Close" onClick={onClose}>
                <X size={16} strokeWidth={STROKE} />
              </button>
            </div>
          </div>
          <div className="o-sheet-body">{body}</div>
          <div className="o-sheet-actions">{actions}</div>
        </div>
      </>
    );
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      {inputs}
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-title">{title}</div>
        {body}
        <div className="dialog-actions" style={{ flexWrap: 'wrap' }}>{actions}</div>
      </div>
    </div>
  );
}

export default PhotoCaptureSheet;
